"use client";

import { useState, useCallback, useRef, type FormEvent } from "react";
import { useTurnstile } from "@/hooks/useTurnstile";

interface FormField {
  label: string;
  type: "text" | "email" | "url" | "textarea" | "file";
  required: boolean;
}

interface CareerApplicationFormProps {
  jobTitle: string;
  jobSlug: string;
  fields: FormField[];
}

export default function CareerApplicationForm({
  jobTitle,
  jobSlug,
  fields,
}: CareerApplicationFormProps) {
  const { containerRef, token, reset } = useTurnstile();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!token) {
        setError("Please complete the verification.");
        return;
      }

      setSubmitting(true);
      setError(null);

      const formData = new FormData(e.currentTarget);
      formData.set("turnstileToken", token);
      formData.set("_jobTitle", jobTitle);
      formData.set("_jobSlug", jobSlug);

      try {
        const res = await fetch("/api/careers/apply", {
          method: "POST",
          body: formData,
        });

        let data: { error?: string; success?: boolean };
        try {
          data = await res.json();
        } catch {
          data = { error: "Unexpected server error. Please try again." };
        }

        if (!res.ok) {
          setError(data.error || "Something went wrong.");
          reset();
        } else {
          setSuccess(true);
        }
      } catch {
        setError("Network error. Please try again.");
        reset();
      } finally {
        setSubmitting(false);
      }
    },
    [token, reset, jobTitle, jobSlug],
  );

  const inputClass =
    "w-full rounded-lg border border-black/10 bg-neutral-50 px-4 py-3 text-sm text-black placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/30 transition-colors disabled:opacity-50";

  if (success) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-8 sm:p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold tracking-tight mb-2">
          Application submitted
        </h3>
        <p className="text-sm text-black/50 font-light">
          Thanks for your interest in joining Hudson Labs. We&apos;ll review your
          application and get back to you soon.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-black/[0.06] p-6 sm:p-8 bg-black/[0.01]">
      <h2 className="text-xl font-[family-name:var(--font-display)] tracking-tight mb-1">
        Apply for this position
      </h2>
      <p className="text-sm text-black/40 font-light mb-6">
        All fields marked with * are required.
      </p>

      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        {fields.map((field) => {
          const name = field.label;

          if (field.type === "textarea") {
            return (
              <div key={name}>
                <label className="block text-sm font-medium text-black/70 mb-1.5">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <textarea
                  name={name}
                  required={field.required}
                  disabled={submitting}
                  rows={3}
                  className={inputClass + " resize-y"}
                />
              </div>
            );
          }

          if (field.type === "file") {
            return (
              <div key={name}>
                <label className="block text-sm font-medium text-black/70 mb-1.5">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <label
                  className={`flex items-center gap-3 cursor-pointer rounded-lg border border-dashed border-black/15 bg-neutral-50 px-4 py-4 text-sm transition-colors hover:border-violet-500/30 hover:bg-violet-50/30 ${submitting ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <svg className="w-5 h-5 text-black/30 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                  <span className="text-black/50 font-light truncate">
                    {fileName || "Upload PDF or Word document (max 5MB)"}
                  </span>
                  <input
                    type="file"
                    name={name}
                    required={field.required}
                    disabled={submitting}
                    accept=".pdf,.doc,.docx"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setFileName(file ? file.name : null);
                    }}
                  />
                </label>
              </div>
            );
          }

          return (
            <div key={name}>
              <label className="block text-sm font-medium text-black/70 mb-1.5">
                {field.label}
                {field.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              <input
                type={field.type}
                name={name}
                required={field.required}
                disabled={submitting}
                className={inputClass}
              />
            </div>
          );
        })}

        {/* Turnstile widget */}
        <div ref={containerRef} className="mt-2" />

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-black py-3 text-sm font-medium text-white hover:bg-black/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Submitting..." : "Submit application"}
        </button>

        <p className="text-xs text-black/30 text-center leading-relaxed">
          Your information will be sent directly to our hiring team.
          Protected by Cloudflare Turnstile.
        </p>
      </form>
    </div>
  );
}
